// ─────────────────────────────────────────────────────────────
// WeatherWidget.js
// Minimal weather forecast pill for an event.
//
// Display: wind icon (colored) | precip icon | temp
// Tap/click expands inline detail text (works on mobile too).
//
// Precip icons:
//   SunIcon       — clear
//   CloudIcon     — cloudy/overcast
//   LightRainIcon — drizzle / slight chance of rain
//   RainIcon      — rain / showers
//   SnowIcon      — snow / sleet
//   StormIcon     — thunderstorm
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';

// ─── WIND HELPERS ─────────────────────────────────────────────
const windColor = (mph) => {
  if (mph < 8)  return '#1a7a1a';
  if (mph < 15) return '#b07d20';
  return '#b52a2a';
};

const windLabel = (mph) => {
  if (mph < 8)  return 'Light wind';
  if (mph < 15) return 'Moderate wind';
  return 'Strong wind';
};

// ─── SVG ICONS ────────────────────────────────────────────────
const S = { display: 'inline-block', verticalAlign: 'middle', flexShrink: 0, width: '14px', height: '14px' };

const WindIcon = ({ mph }) => {
  const c = windColor(mph);
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={S}>
      <path d="M2 5 Q7 3 10 5 Q13 7 16 5"  stroke={c} strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M2 9 Q6 7 9 9 Q12 11 15 9"  stroke={c} strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M2 13 Q5 11 8 13 Q11 15 13 13" stroke={c} strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
};

const SunIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={S}>
    <circle cx="8" cy="8" r="3" fill="#b07d20"/>
    {[0,45,90,135,180,225,270,315].map((deg, i) => {
      const r = deg * Math.PI / 180;
      return <line key={i}
        x1={8 + 4.5*Math.cos(r)} y1={8 + 4.5*Math.sin(r)}
        x2={8 + 6.5*Math.cos(r)} y2={8 + 6.5*Math.sin(r)}
        stroke="#b07d20" strokeWidth="1.4" strokeLinecap="round"/>;
    })}
  </svg>
);

// Generic cloud shape used by all cloud-based icons
const CloudPath = ({ fill = '#7a8fa6' }) => (
  <path d="M4.5 10 Q2 10 2 7.5 Q2 5.2 4.2 5.2 Q4.4 3 6.8 3 Q8.6 3 9.5 4.3 Q10.3 3.4 11.6 3.4 Q14 3.4 14 5.8 Q15.4 6.2 15.4 7.6 Q15.4 10 13 10 Z"
    fill={fill}/>
);

const CloudIcon = () => (
  <svg width="18" height="14" viewBox="0 0 18 14" fill="none" style={S}>
    <CloudPath />
  </svg>
);

// Light rain: cloud + single small drop
const LightRainIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={S}>
    <CloudPath />
    <line x1="8.5" y1="12" x2="8" y2="15.5"
      stroke="#2d6da8" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);

// Rain: cloud + three drops
const RainIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={S}>
    <CloudPath />
    <line x1="5.5" y1="12" x2="5"   y2="15.5" stroke="#2d6da8" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="9"   y1="12" x2="8.5" y2="16"   stroke="#2d6da8" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="12.5" y1="12" x2="12" y2="15.5" stroke="#2d6da8" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const SnowIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={S}>
    <CloudPath />
    {[[5.5,13],[9,14.5],[12.5,13],[7,16.5],[11,16.5]].map(([x,y],i) => (
      <circle key={i} cx={x} cy={y} r="1.1" fill="#2d6da8"/>
    ))}
  </svg>
);

const StormIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={S}>
    <CloudPath fill="#4a5f72"/>
    <path d="M10 10.5 L7.5 14 L9.5 14 L8 17.5 L12.5 12 L10.5 12 Z" fill="#b07d20"/>
  </svg>
);

// ─── PRECIP CLASSIFICATION ────────────────────────────────────
const getPrecipIcon = (forecast) => {
  if (!forecast) return <SunIcon />;
  const f = forecast.toLowerCase();
  // Storm — check first
  if (f.includes('thunder') || f.includes('storm'))
    return <StormIcon />;
  // Snow
  if (f.includes('snow') || f.includes('sleet') || f.includes('flurr'))
    return <SnowIcon />;
  // Light rain — NWS uses "Slight Chance", "Chance", "Isolated", "Drizzle", "Light Rain"
  // Must check BEFORE general rain since these strings also contain "rain"/"shower"
  if (f.includes('drizzle') ||
      f.includes('slight chance') ||
      f.includes('isolated') ||
      f.includes('light rain') ||
      (f.includes('chance') && !f.includes('likely') && !f.includes('heavy')))
    return <LightRainIcon />;
  // Regular rain
  if (f.includes('rain') || f.includes('shower') || f.includes('precipitation'))
    return <RainIcon />;
  // Clouds
  if (f.includes('cloud') || f.includes('overcast') || f.includes('fog') || f.includes('mist'))
    return <CloudIcon />;
  return <SunIcon />;
};

const Sep = () => (
  <span style={{
    display: 'inline-block', width: '1px', height: '10px',
    background: 'var(--border)', flexShrink: 0
  }}/>
);

// ─── COMPONENT ────────────────────────────────────────────────
function WeatherWidget({ eventDate, lat, lng, inline }) {
  const [weather, setWeather]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!lat || !lng || !eventDate) { setLoading(false); return; }
    const diffDays = (eventDate - new Date()) / (1000 * 60 * 60 * 24);
    if (diffDays < 0 || diffDays > 7) { setLoading(false); return; }
    fetchWeather();
  }, [lat, lng, eventDate?.getTime()]);

  const fetchWeather = async () => {
    try {
      const ptRes = await fetch(
        `https://api.weather.gov/points/${lat.toFixed(4)},${lng.toFixed(4)}`,
        { headers: { 'User-Agent': 'UltimateRSVP/1.0' } }
      );
      if (!ptRes.ok) throw new Error('NWS point failed');
      const { gridId, gridX, gridY } = (await ptRes.json()).properties;

      const fcRes = await fetch(
        `https://api.weather.gov/gridpoints/${gridId}/${gridX},${gridY}/forecast/hourly`,
        { headers: { 'User-Agent': 'UltimateRSVP/1.0' } }
      );
      if (!fcRes.ok) throw new Error('NWS forecast failed');
      const periods = (await fcRes.json()).properties.periods;

      // Find closest hourly period to event time
      const target = eventDate.getTime();
      const closest = periods.reduce((best, p) =>
        Math.abs(new Date(p.startTime) - target) < Math.abs(new Date(best.startTime) - target)
          ? p : best
      );

      // NWS windSpeed is "X mph" or "X to Y mph" — take the higher value
      const nums = (closest.windSpeed || '0 mph').match(/\d+/g) || ['0'];
      setWeather({
        windMph:       parseInt(nums[nums.length - 1]),
        shortForecast: closest.shortForecast,
        temp:          closest.temperature,
        tempUnit:      closest.temperatureUnit,
      });
    } catch (err) {
      console.error('Weather fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <span className="weather-widget weather-loading">···</span>
  );
  if (!weather) return null;

  const detailText = `${windLabel(weather.windMph)} (${weather.windMph} mph) · ${weather.shortForecast}`;

  if (inline) {
    return (
      <span style={{fontSize:'12px', color:'rgba(255,255,255,.85)', fontWeight:500}}>
        {getPrecipIcon(weather.shortForecast)} {weather.temp}°{weather.tempUnit}
      </span>
    );
  }

  return (
    <span
      className={`weather-widget${expanded ? ' weather-expanded' : ''}`}
      onClick={() => setExpanded(p => !p)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && setExpanded(p => !p)}
      title={detailText}
    >
      <WindIcon mph={weather.windMph} />
      <Sep />
      {getPrecipIcon(weather.shortForecast)}
      <Sep />
      <span className="weather-temp">{weather.temp}°{weather.tempUnit}</span>
      {expanded && (
        <span className="weather-detail">{detailText}</span>
      )}
    </span>
  );
}

export default WeatherWidget;
